import { Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useLocale } from '../../../lib/locale-context';
import { periodRange, timeAmount, totalMinutes, winFigure, type WebWin } from '../../../lib/wins';
import { formatWinDate, useFigureCopy, WinSurface, type WinCardLayout } from './WinCard';
import styles from './WinCard.module.css';

const ROWS = 4;

export function recapCountLabel(t: (key: string, opts?: Record<string, unknown>) => string, count: number): string {
  return t(count === 1 ? 'wins.recap.countOne' : 'wins.recap.count', { count });
}

/** The Weekly / Monthly summary that sits before the period's wins. */
export function RecapCard({
  period,
  wins,
  now,
  layout,
  onShare,
}: {
  period: 'week' | 'month';
  wins: WebWin[];
  now: Date;
  layout: WinCardLayout;
  onShare?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const copy = useFigureCopy();
  const { start, end } = periodRange(period, now);
  const last = new Date(end.valueOf() - 1);
  const title =
    period === 'week'
      ? t('wins.recap.week')
      : t('wins.recap.month', { month: start.toLocaleDateString(locale, { month: 'long' }) });
  const range = `${formatWinDate(start.toISOString(), locale)} – ${formatWinDate(last.toISOString(), locale)}`;
  const minutes = totalMinutes(wins);
  const time = minutes > 0 ? timeAmount(minutes) : null;
  const rows = wins.slice(0, ROWS);

  return (
    <WinSurface tone={period} layout={layout} showShare onShare={onShare} shareLabel={t('wins.card.share')}>
      <div className={styles.recapHead}>
        <span className="sym-type-small-semi-bold">{title}</span>
        <span className={`sym-type-tiny-regular ${styles.when}`}>{range}</span>
      </div>
      <div className={styles.recapMain}>
        <div className={styles.recapLeft}>
          <div className={styles.fig}>{recapCountLabel(t, wins.length)}</div>
          {time && (
            <Typography variant="h3" color="inherit" className={styles.label}>
              {t('wins.recap.time', { time: copy[time.unit](new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(time.amount)) })}
            </Typography>
          )}
        </div>
        {rows.length > 0 && (
          <ul className={styles.recapRows}>
            {rows.map((w) => {
              const figure = winFigure(w, copy, locale);
              return (
                <li key={w.id}>
                  {figure ? (
                    <>
                      <span className={styles.rfig}>{figure.value}</span>
                      <span>{figure.label}</span>
                    </>
                  ) : (
                    <span>{w.title}</span>
                  )}
                </li>
              );
            })}
            {wins.length > ROWS && <li className={styles.more}>{t('wins.recap.more', { count: wins.length - ROWS })}</li>}
          </ul>
        )}
      </div>
    </WinSurface>
  );
}
